import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Locality } from '../api/types';

interface Props {
  id: string;
  value: string;
  options: Locality[];
  placeholder?: string;
  className?: string;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
}

export function LocationAutocomplete({
  id,
  value,
  options,
  placeholder = 'Search a locality or city',
  className = 'form-control',
  onChange,
  onCommit,
}: Props) {
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  const query = value.trim().toLocaleLowerCase();
  const suggestions = query.length === 0
    ? []
    : options
        .filter((option) =>
          option.locality.toLocaleLowerCase().includes(query)
          || option.city.toLocaleLowerCase().includes(query),
        )
        .slice(0, 8);
  const listOpen = focused && query.length > 0;
  const listId = `${id}-suggestions`;

  useEffect(() => {
    const handleOutsidePointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setFocused(false);
    };
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => document.removeEventListener('pointerdown', handleOutsidePointer);
  }, []);

  const choose = (option: Locality) => {
    onChange(option.locality);
    onCommit(option.locality);
    setActiveIndex(-1);
    setFocused(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === 'Escape') {
      setFocused(false);
      setActiveIndex(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0 && suggestions[activeIndex]) choose(suggestions[activeIndex]);
      else {
        onCommit(value.trim());
        setFocused(false);
      }
    }
  };

  return (
    <div ref={rootRef} className="sf-location-autocomplete">
      <input
        id={id}
        className={className}
        type="search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listOpen}
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onFocus={() => setFocused(true)}
        onChange={(event) => {
          onChange(event.target.value);
          setActiveIndex(-1);
        }}
        onBlur={() => {
          onCommit(value.trim());
          setActiveIndex(-1);
        }}
        onKeyDown={handleKeyDown}
      />
      {listOpen && (
        <ul id={listId} className="sf-location-autocomplete__list" role="listbox" aria-label="Place suggestions">
          {suggestions.length > 0 ? suggestions.map((option, index) => (
            <li
              id={`${listId}-${index}`}
              key={`${option.city}-${option.locality}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`sf-location-autocomplete__option${index === activeIndex ? ' is-active' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              <span className="sf-location-autocomplete__pin" aria-hidden="true">⌖</span>
              <span className="sf-location-autocomplete__place">
                <strong>{option.locality}</strong>
                <small>{option.city} · {option.listingCount} listing{option.listingCount === 1 ? '' : 's'}</small>
              </span>
            </li>
          )) : (
            <li className="sf-location-autocomplete__empty" role="option" aria-selected="false">
              No matching RoomKart places. Press Enter to search this text.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
